import Highcharts from "highcharts";
import HighchartsReact, { HighchartsReactRefObject } from "highcharts-react-official";
import { useContext, useMemo, useRef } from "react";
import { styled } from "@mui/material";

import { useTranslation } from "@/lib/i18n";
import { LineGraphEdrLineAllOfColorEnum } from "@/lib/api";
import { WidgetContentContext } from "@/features/dashboard/components/widget-container-card";
import { useSelectedTimeRange } from "@/features/dashboard/store";
import { GraphProps } from "@/features/shared/widgets/graphs/graph.types";
import { useChartSizes } from "@/features/shared/widgets/graphs/hooks/useChartSizes";
import { usePrimaryUnit } from "@/features/shared/widgets/graphs/hooks/usePrimaryUnit";
import { useThresholdsY } from "@/features/shared/widgets/graphs/hooks/useThresholdsY";
import { useThresholdsX } from "@/features/shared/widgets/graphs/hooks/useThresholdsX";
import { useThresholdZones } from "@/features/shared/widgets/graphs/hooks/useThesholdZones";
import NoDataLabel from "@/features/shared/widgets/graphs/no-data-label";
import { useLineGraphNoData } from "@/features/shared/widgets/graphs/hooks/use-line-graph-no-data";
import useLayoutStore from "@/store/useLayoutStore";
import getLineWidth from "@/lib/graph/get-line-width";
import getDashStyle from "@/lib/graph/get-dash-style";

import baseOptions from "./base-options";
import {
  BLUE,
  colorGenerator,
  getFormattedValue,
  getYAxisPerUnit as getYAxisForUnit,
  GREEN,
} from "./helpers";

const Linegraph = ({ data, configuration, options, currentTime }: GraphProps) => {
  const { t } = useTranslation();
  const chartRef = useRef<HighchartsReactRefObject>(null);
  const widgetContentContext = useContext(WidgetContentContext);
  const widgetContentRef = widgetContentContext.ref;

  const [isFullscreen] = useLayoutStore((s) => s.context.isFullscreen);

  const chartHeight = useChartSizes(chartRef, widgetContentRef, isFullscreen ?? false);

  const timeRange = useSelectedTimeRange();
  const timeStart = timeRange.timeStart;
  const timeEnd = timeRange.timeEnd;

  const primaryUnit = usePrimaryUnit(data);
  const primaryUnitType = useMemo(
    () => (primaryUnit?.type === "UNKNOWN" ? primaryUnit.symbol : primaryUnit?.type),
    [primaryUnit],
  );

  // Horizontal line and band plots
  const [yThresholdLines, yThresholdPlots] = useThresholdsY(configuration);
  // calculate plot bands for x axis (where points exceed thresholds)
  const [xThresholdPlots, xThresholdLines] = useThresholdsX(configuration, data);
  // defines zones to color points that exceed thresholds
  const zones = useThresholdZones(configuration);

  const yAxis = useMemo(() => {
    const y = getYAxisForUnit(primaryUnitType);
    y[0] = {
      ...baseOptions.yAxis,
      ...y[0],
      title: {
        text: primaryUnit?.symbol,
      },
      plotLines: yThresholdLines,
      plotBands: yThresholdPlots,
    };

    if (options?.yAxis) {
      Array.isArray(options.yAxis)
        ? options.yAxis.forEach((axis, i) => (y[i] = { ...y[i], ...axis }))
        : (y[0] = { ...y[0], ...options.yAxis });
    }

    return y;
  }, [data, primaryUnit, primaryUnitType, configuration, yThresholdLines, yThresholdPlots]);

  const chartData: Highcharts.Options | undefined = useMemo(() => {
    if (!data || !configuration) return undefined;

    // Create generators for lines to get the rights colors
    const greenGenerator = colorGenerator(GREEN);
    const blueGenerator = colorGenerator(BLUE);
    const getColors = (key: LineGraphEdrLineAllOfColorEnum) => {
      if (key === LineGraphEdrLineAllOfColorEnum.Green) {
        return greenGenerator.next().value;
      }
      if (key === LineGraphEdrLineAllOfColorEnum.Blue) {
        return blueGenerator.next().value;
      }

      return undefined;
    };

    const linegraphOptions: Highcharts.Options = {
      ...baseOptions,
      series: [
        // add series for each line
        ...(data.lines?.map((line) => {
          const lineConfig = configuration.lines.find((config) => config.id == line.id);
          const points =
            line.data?.datapoints.filter((point) => point.value !== null) ?? [];
          const color =
            points.length === 0
              ? "grey"
              : getColors(lineConfig?.color ?? LineGraphEdrLineAllOfColorEnum.Blue);
          const dashStyle = getDashStyle(lineConfig?.line_style);
          const lineWidth = getLineWidth(lineConfig?.line_width);

          return {
            type: "line",
            name:
              points.length === 0
                ? `${lineConfig?.title} [not available]`
                : lineConfig?.title,
            description: line.data?.parameter.description,
            data:
              line.data?.datapoints?.map((datapoint) => ({
                x: +new Date(datapoint.timestamp),
                y: getFormattedValue(datapoint.value, line.data?.parameter),
              })) ?? [],

            lineWidth,
            dashStyle,
            color,
            marker: {
              symbol: lineConfig?.symbol,
              radius: 4,
              enabledThreshold: 0,
            },

            tooltip: {
              valueSuffix: line.data?.parameter.unit.symbol,
            },
            zones,
            states: {
              inactive: {
                // disable fading over other series on hover
                opacity: 1,
              },
            },
          } as Highcharts.SeriesOptionsType;
        }) ?? []),
      ],

      xAxis: {
        type: "datetime",
        min: timeStart ? +timeStart : undefined,
        max: timeEnd ? +timeEnd : undefined,
        startOfWeek: 0,
        plotLines: currentTime
          ? [
              {
                id: "currentTime",
                color: "#FF0000",
                value: +currentTime,
              },
              ...xThresholdLines,
            ]
          : xThresholdLines,
        plotBands: xThresholdPlots,
      },
      yAxis,

      chart: {
        ...baseOptions.chart,
        height: chartHeight,
        animation: {
          duration: 100,
        },
        events: {
          render: (event) => {
            const chart = event.target as unknown as Highcharts.Chart;

            if (!chart) return;

            function distance(a: Highcharts.Point, b: Highcharts.Point) {
              const xAxis = chart.xAxis[0];
              const yAxis = chart.yAxis[0];

              if (!a.y || !b.y) return undefined;

              const xa = xAxis.toPixels(a.x);
              const ya = yAxis.toPixels(a.y);
              const xb = xAxis.toPixels(b.x);
              const yb = yAxis.toPixels(b.y);

              return Math.sqrt(Math.pow(xb - xa, 2) + Math.pow(yb - ya, 2));
            }

            const markerRadius = chart.options.plotOptions?.series?.marker?.radius || 4;
            const minPixelDistance = markerRadius * 2.5;

            let shouldRedraw = false;
            chart.series.forEach((series) => {
              const nonEmptyPoints = series.points.filter(
                (point) => point.y !== null && point.y !== undefined,
              );

              nonEmptyPoints.forEach((point, index) => {
                const previousEnabledPoints = nonEmptyPoints
                  .slice(0, index)
                  .filter((point) => point?.options?.marker?.enabled);

                const shouldEnable = previousEnabledPoints.every((otherPoint) => {
                  const d = distance(point, otherPoint);
                  if (d === undefined) return true;
                  return d > minPixelDistance;
                });

                if (point?.options?.marker?.enabled !== shouldEnable) {
                  point.update(
                    {
                      ...point.options,
                      marker: { enabled: shouldEnable },
                    },
                    false, // redraw
                    false, // animation
                  );
                  shouldRedraw = true;
                }
              });
            });
            if (shouldRedraw) {
              chart.redraw();
            }
          },
        },
        ...options?.chart,
      },
    };
    return linegraphOptions;
  }, [
    data,
    yAxis,
    configuration,
    primaryUnit,
    currentTime,
    timeStart,
    timeEnd,
    chartHeight,
  ]);

  const noData = useLineGraphNoData(data);

  return (
    <HighChartsStyled>
      <HighchartsReact
        ref={chartRef}
        highcharts={Highcharts}
        options={chartData}
        containerProps={{
          style: {
            height: "100%",
            width: "100%",
          },
        }}
      />
      {noData && <NoDataLabel>{t("general.no_data")}</NoDataLabel>}
    </HighChartsStyled>
  );
};

const HighChartsStyled = styled("div")({
  position: "relative",
  ".chart__show-on-hover": {
    opacity: 0,
    transition: "opacity .2s ease",
  },
  "&:hover .chart__show-on-hover": {
    "@container widget (height > 400px)": {
      opacity: 0.5,
    },
  },
});

export default Linegraph;
